import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCheckCircle,
  faTimesCircle,
} from '@fortawesome/free-regular-svg-icons';
import {
  faCircleNotch,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18n from '../i18n.js';
import './App.css';

// Set up Font Awesome
library.add(faCheckCircle, faCircleNotch, faTimesCircle);

const App = (props) => {

  const [emailValid, setEmailValid] = useState(false);
  const [emailToSignup, setEmailToSignup] = useState('');
  const [emailDisabled, setEmailDisabled] = useState(false);

  const [signupStatus, setSignupStatus] = useState(false);
  const [waitingForLoad, setWaitingForLoad] = useState(true);
  const [alreadySignedUp, setAlreadySignedUp] = useState(false);
  const [signupError, setSignupError] = useState(false);

  const firstUpdate = useRef(true);

  // Get URL bar
  const location = useLocation();

  const getSubscriptionToUpdate = (params) => {

    const queryParams = new URLSearchParams(location.search);
    const sfSubscription = queryParams.get('sfSubscription');

    if (sfSubscription){
      return sfSubscription;
    } else{
      return params.sfSubscription;
    }

  }

  const getSuccessMessage = (params) => {

    const queryParams = new URLSearchParams(location.search);
    const signupSuccess = queryParams.get('signupsuccess');

    if (signupSuccess){
      return signupSuccess;
    } else{
      return params.signupSuccess;
    }

  }


  const getSignupAlready = (params) => {

    const queryParams = new URLSearchParams(location.search);
    const signupAlready = queryParams.get('signupalready');

    if (signupAlready){
      return signupAlready;
    } else{
      return params.signupAlready;
    }

  }

  const getLeadSourceDescription = (params) => {

    const queryParams = new URLSearchParams(location.search);
    const leadSourceDescription = queryParams.get('leadSourceDescription');

    if (leadSourceDescription){
      return leadSourceDescription;
    } else{
      return params.leadSourceDescription;
    }

  }

  const subscriptionToUse = getSubscriptionToUpdate(props.params);
  const signupSuccessToUse = getSuccessMessage(props.params);
  const signupAlreadyToUse = getSignupAlready(props.params);
  const leadSourceDescription = getLeadSourceDescription(props.params);

  // Get subscription for a given email and token if we are logged in (and provided to the app)
  const getSubscription = useCallback( async (params) => {

    // Show the waiting circle
    setWaitingForLoad(true);

    let headers;
    if (params.token) {
      headers = {
        Authorization: params.token &&
          `Token ${params.token}`,
      };
    }

    const request = {
      method: 'GET',
      url: `${params.dataspanApiUrl}/websitesubs/user_subscriptions/`,
      params: {
        email: params.email,
        wtRegion: params.wtRegion,
      },
      headers: headers,      
    };

    await axios(request)
      .then(response => {

        // Check if the data array exists in the response
        if (response.status === 200) {

          // Find matching fields for the subscription name
          const matchedFields = response.data.subscriptions ? response.data.subscriptions.filter( field => field.apiField === subscriptionToUse) : null;

          // If that subscription is already checked then show already signed up message
          if (matchedFields.length > 0 && matchedFields[0].subscribed === true) {
            setAlreadySignedUp(true);
          } else {
            // IF not checked then show the form with the email disabled
            validateEmail(params.email);
            setEmailDisabled(true);
          }

        }

        // Turn off the waiting circle
        setWaitingForLoad(false);
      })
      .catch(error => {
        console.log(error);

        // Add the email to the form
        validateEmail(params.email);

        // Turn off the waiting circle
        setWaitingForLoad(false);
      });
  }, [subscriptionToUse] );



  // Set up Module details
  useEffect(() => {

    // If the email is available then check if user is already subscribed
    if (firstUpdate && props.params.email && props.params.email !== i18n.t('email-invalid')) {
      getSubscription(props.params);
      firstUpdate.current = false;
    } else if (firstUpdate){
      setWaitingForLoad(false);
      firstUpdate.current = false;
    }

  }, [props.params, getSubscription]);

  const validateEmail = (email) => {
   
    if (isEmail(email)) {
      setEmailValid(true);      
    } else {
      setEmailValid(false);
    }
    setEmailToSignup(email);
  }

  // Update the subscription for the subscription field name provided on startup
  const updateSubscription = () => {

    const params = props.params;

    // Show the waiting circle
    setWaitingForLoad(true);

    let headers;
    if (params.token) {
      headers = {
        Authorization: params.token &&
          `Token ${params.token}`,
      };
    }

    const request = {
      method: 'POST',
      url: `${params.dataspanApiUrl}/websitesubs/signup_single_subscription/`,
      data: {
        "email": emailToSignup,
        "sfSubscription": subscriptionToUse,
        "leadSourceDescription": leadSourceDescription,
      },
      headers: headers,      
    };

    return axios(request)
      .then(response => {

        // Check if the data array exists in the response
        if (response.status === 200) {
          setSignupStatus(true);
        } else if (response.status === 204){
          setAlreadySignedUp(true);
        } else {
          setSignupError(true);
        }

        // Turn off the waiting circle
        setWaitingForLoad(false);
      })
      .catch(error => {
        console.log(error);
        // Turn off the waiting circle
        setWaitingForLoad(false);
        setSignupError(true);
      });
  }

  const signupForm = <div className='signup-form'>
    <div className='email-input'>
      <input
        type="text"
        id="userEmail"
        onChange={(e) => validateEmail(e.target.value)}
        placeholder={i18n.t('email-placeholder')}
        value={emailToSignup}
        disabled={emailDisabled}
      >
      </input>
    </div>
    <div className='submit-button'>
      <button
        disabled={!emailValid}
        onClick={() => updateSubscription()}
      >
        {i18n.t('submit')}
      </button>
    </div>
  </div>;

  const successMessage = <div className='success-message'>
    <div>
      <FontAwesomeIcon icon={["far", "check-circle"]} size="2x" className='check' />
    </div>
    <div>{signupSuccessToUse}</div>
  </div>

  const alreadySignedUpMessage = <div className='success-message'>
    <div>
      <FontAwesomeIcon icon={["far", "check-circle"]} size="2x" className='check' />
    </div>
    <div>{signupAlreadyToUse}</div>
  </div>

  const signupErrorMessage = <div className='success-message'>
    <div>
      <FontAwesomeIcon icon={["far", "times-circle"]} size="2x" className='check' />
    </div>
    <div>{i18n.t('sign-up-error')}</div>
  </div>

  const waiting = <div className='waiting'>
    <FontAwesomeIcon icon="circle-notch" size="3x" className="waiting-icon" spin />
  </div>;

  // Set up the right content
  let contentToDisplay;
  if (waitingForLoad) {
    contentToDisplay = waiting;
  } else if (signupStatus) {
    contentToDisplay = successMessage;
  } else if (alreadySignedUp) {
    contentToDisplay = alreadySignedUpMessage;
  } else if (signupError) {
    contentToDisplay = signupErrorMessage;
  } else {
    contentToDisplay = signupForm;
  }

  return (
    <div className='app'>
      <div className='subscription-signup'>

        { contentToDisplay }

      </div>
    </div>
  );

}

export default App;
